import { z } from 'zod'

import { pruned } from '@/lib/filters'

import { skuDocumentsSearchFields } from './skuDocumentsSearch'

/** VentaHub's two tabs: the SKUs that lost money, and every SKU's profit. */
export const LOSS_TABS = ['loss', 'profitability'] as const
export type LossTab = (typeof LOSS_TABS)[number]

export const LOSS_PAGE_SIZES = [25, 50, 100] as const
export const DEFAULT_LOSS_PAGE_SIZE = 50

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const ids = z.array(z.number().int().positive()).max(50).optional().catch(undefined)

/**
 * Loss Making SKUs' URL: the tab; the reports' scope (warehouses, the
 * marketplace, its stores, brands) and the range; whether the report has
 * been run for them; the search, the sort and the page. Every field
 * optional; defaults apply at read time.
 */
export const lossMakingSearchSchema = z.object({
  tab: z
    .enum(LOSS_TABS)
    .optional()
    .catch(undefined)
    .describe('Which SKUs: loss (Loss making SKUs) or profitability (every SKU). Default loss.'),
  warehouseIds: ids.describe('Warehouses to read, by id; empty means every warehouse.'),
  marketplaceId: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The marketplace to read, by id; absent means every marketplace.'),
  storeIds: ids.describe('Stores to include, by id; empty means every store.'),
  brandIds: ids.describe('Brands to include, by id; empty means every brand.'),
  dateFrom: day
    .optional()
    .catch(undefined)
    .describe('First day of the range, YYYY-MM-DD; the last 30 days when absent.'),
  dateTo: day
    .optional()
    .catch(undefined)
    .describe('Last day of the range, YYYY-MM-DD, inclusive; today when absent.'),
  // Set by Find losses / Show profitability: nothing loads until it is.
  go: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe(
      '1 once Find losses / Show profitability is pressed; nothing loads without it. Any filter or date change clears it.',
    ),
  q: z
    .string()
    .max(100)
    .optional()
    .catch(undefined)
    .describe('Search text over the rows (SKU code, name, brand or store).'),
  sort: z
    .string()
    .regex(/^[a-z0-9_]+$/)
    .max(64)
    .optional()
    .catch(undefined)
    .describe('The column id the rows are sorted by; absent is the report’s own order.'),
  order: z
    .enum(['asc', 'desc'])
    .optional()
    .catch(undefined)
    .describe('Sort direction; asc when a sort is set.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the table, from 1; default 1.'),
  limit: z
    .union([z.literal(25), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Rows per page; default 50.'),
  // The SKU whose orders are open in the drawer, its tab and page.
  ...skuDocumentsSearchFields,
})

export type LossMakingSearch = z.infer<typeof lossMakingSearchSchema>

export const EMPTY_LOSS_MAKING_SEARCH: LossMakingSearch = lossMakingSearchSchema.parse({})

/** Reset: every field cleared, so the page is as it first opened. */
export const RESET_LOSS_MAKING: Partial<LossMakingSearch> = Object.fromEntries(
  Object.keys(lossMakingSearchSchema.shape).map((key) => [key, undefined]),
)

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutLossMakingDefaults(search: LossMakingSearch): LossMakingSearch {
  return pruned({
    ...search,
    tab: search.tab === 'loss' ? undefined : search.tab,
    limit: search.limit === DEFAULT_LOSS_PAGE_SIZE ? undefined : search.limit,
  })
}

/**
 * VentaHub's longest range for this page: a full quarter. The report reads
 * every row of the range to find the loss-makers, so the range is the only
 * thing that bounds the work.
 */
export const MAX_LOSS_RANGE_DAYS = 92

/** The days a range covers, both ends counted, as VentaHub counted them. */
export function rangeDays(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000) + 1
}
